import EcoImg from 'eco-design/base/eco-img';
import EcoLink from 'eco-design/base/eco-link';
import { BhHeaderV2Lang } from 'eco-design/section-layout/ef-header-v2/BhHeaderV2Lang';
import getLocal from 'eco-design-reliance/getLocal';
import gtm from 'eco-design-reliance/gtm';
import React, { useMemo, useState } from 'react';
import { getUrlGtm } from '@/utils/gtmUtils';
import { getTarget } from '../common';
import './index.scss';

interface Props {
    prefixCls: string;
    item: any;
    title: string;
}

const getValidIndex = (list: any[] = [], index: number) => {
    if (!list.length) return -1;
    if (index < 0) return 0;
    if (index >= list.length) return 0;
    return index;
};

const ThirdLinkArrow = ({ className }: { className: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M5.08366 1.91663L9.41699 6.24996L5.08366 10.5833" stroke="#444444" strokeWidth="1.1375" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ProductCard = ({ prefixCls, product, secondItem, thirdItem, title, index }: any) => {
    const { locale = '' } = getLocal();
    const t = BhHeaderV2Lang[locale] || BhHeaderV2Lang['en'];
    const linkUrl = product.learnMoreUrl || product.buyUrl;
    const label = product.label ? t.label?.[product.label] || product.label : '';

    return (
        <div
            className={`${prefixCls}-card-type-e__product`}
            style={{
                animation: `0.45s ease ${index * 0.04}s both productFadeIn`,
            }}
        >
            {linkUrl ? (
                <EcoLink
                    className={`${prefixCls}-card-type-e__product-img-link GtmModuleData-btn-href type-e-product-img`}
                    href={linkUrl}
                    target={getTarget(linkUrl)}
                    dataLayer={{
                        event: 'uaEvent',
                        event_name: 'top_fourth_navigation',
                        position: 'Header Navigation',
                        navigation_name: `${title}_${secondItem?.name || ''}_${thirdItem?.name || ''}_${product.heading}`,
                        list_name: thirdItem?.name,
                        __btn_id1: `type-e-product-img`,
                        __btn_id2: linkUrl,
                        __btn_type: 'href',
                    }}
                >
                    <EcoImg className={`${prefixCls}-card-type-e__product-img`} pc={product.pictureUrl} />
                </EcoLink>
            ) : (
                <EcoImg className={`${prefixCls}-card-type-e__product-img`} pc={product.pictureUrl} />
            )}
            {label && <div className={`${prefixCls}-card-type-e__product-label`}>{label}</div>}
            <div className={`${prefixCls}-card-type-e__product-title`}>{product.heading}</div>
            {product.subtitle && <div className={`${prefixCls}-card-type-e__product-desc`}>{product.subtitle}</div>}
            {(product.learnMoreUrl || product.buyUrl) && (
                <div className={`${prefixCls}-card-type-e__product-btns`}>
                    {product.learnMoreUrl && (
                        <EcoLink
                            className={`${prefixCls}-card-type-e__product-more GtmModuleData-btn-href type-e-product-more`}
                            href={product.learnMoreUrl}
                            target={getTarget(product.learnMoreUrl)}
                            dataLayer={{
                                navigation_name: `${title}_${secondItem?.name || ''}_${thirdItem?.name || ''}_${product.heading}`,
                                position: 'Header Navigation',
                                ...getUrlGtm(product.learnMoreUrl, locale, {
                                    product_name: product.heading,
                                    list_name: thirdItem?.name,
                                    button_name: t.more,
                                }),
                                __btn_id1: `type-e-product-more`,
                                __btn_id2: product.learnMoreUrl,
                                __btn_type: 'href',
                            }}
                        >
                            {t.more2}
                        </EcoLink >
                    )}
                    {
                        product.buyUrl && (
                            <EcoLink
                                className={`${prefixCls}-card-type-e__product-buy GtmModuleData-btn-href type-e-product-buy`}
                                href={product.buyUrl}
                                target={getTarget(product.buyUrl)}
                                dataLayer={{
                                    navigation_name: `${title}_${secondItem?.name || ''}_${thirdItem?.name || ''}_${product.heading}`,
                                    position: 'Header Navigation',
                                    ...getUrlGtm(product.buyUrl, locale, {
                                        product_name: product.heading,
                                        list_name: thirdItem?.name,
                                        button_name: t.buy,
                                    }),
                                    __btn_id1: `type-e-product-buy`,
                                    __btn_id2: product.buyUrl,
                                    __btn_type: 'href',
                                }}
                            >
                                {t.buy2}
                            </EcoLink >
                        )
                    }
                </div >
            )}
        </div >
    );
};

const CardTypeE: React.FC<Props> = ({ prefixCls, item, title }) => {
    const [openSecondIndex, setOpenSecondIndex] = useState(0);
    const [activeThirdIndexMap, setActiveThirdIndexMap] = useState<Record<number, number>>({ 0: 0 });
    const [openThirdKey, setOpenThirdKey] = useState('');
    const [expandedProductKey, setExpandedProductKey] = useState('');

    const secondItems = useMemo(() => item?.subs || [], [item]);
    const activeSecondItem = secondItems[getValidIndex(secondItems, openSecondIndex)] || {};
    const thirdItems = activeSecondItem?.subs || [];
    const activeThirdIndex = activeThirdIndexMap[openSecondIndex] || 0;
    const activeThirdItem = thirdItems[getValidIndex(thirdItems, activeThirdIndex)] || {};
    const productItems = activeThirdItem?.subs || [];

    const handleSecondActive = (index: number, secondItem: any) => {
        setOpenSecondIndex(index);
        setOpenThirdKey('');
        setExpandedProductKey('');
        setActiveThirdIndexMap((prev) => ({
            ...prev,
            [index]: prev[index] || 0,
        }));
        gtm.push({
            event: 'uaEvent',
            event_name: 'top_second_navigation',
            navigation_name: `${title}_${secondItem.name}`,
            __btn_id1: `header-nvd-t2-i`,
            __btn_id2: `${String(title).toLowerCase().trim()}_${String(secondItem.name).toLowerCase().trim()}`,
            __btn_type: 'common',
        });
    };

    const handleThirdActive = (secondIndex: number, index: number, secondItem: any, thirdItem: any) => {
        setOpenSecondIndex(secondIndex);
        setActiveThirdIndexMap((prev) => ({
            ...prev,
            [secondIndex]: index,
        }));
        gtm.push({
            event: 'uaEvent',
            event_name: 'top_third_navigation',
            navigation_name: `${title}_${secondItem?.name || ''}_${thirdItem.name}`,
            __btn_id1: `header-nvd-t3-i`,
            __btn_id2: `${String(title).toLowerCase().trim()}_${String(thirdItem.name).toLowerCase().trim()}`,
            __btn_type: 'common',
        });
    };

    if (secondItems.length === 0) return <div className={`${prefixCls}-card-type-e`}></div>;

    return (
        <div className={`${prefixCls}-card-type-e`}>
            <div className={`${prefixCls}-card-type-e__content`}>
                <div className={`${prefixCls}-card-type-e__main`}>
                    <div className={`${prefixCls}-card-type-e__left`}>
                        {secondItems.map((secondItem: any, secondIndex: number) => {
                            const thirdList = secondItem?.subs || [];
                            const isOpen = openSecondIndex === secondIndex;
                            const currentThirdIndex = activeThirdIndexMap[secondIndex] || 0;
                            return (
                                <div className={`${prefixCls}-card-type-e__second`} key={secondItem.id || secondIndex}>
                                    <button
                                        className={`${prefixCls}-card-type-e__second-btn ${isOpen ? 'active' : ''} GtmModuleData-btn-common header-nvd-t2-i`}
                                        data-btn-id={`${String(title).toLowerCase().trim()}_${String(secondItem.name).toLowerCase().trim()}`}
                                        type="button"
                                        onClick={() => handleSecondActive(secondIndex, secondItem)}
                                    >
                                        {secondItem.name}
                                    </button>
                                    {isOpen && (
                                        <div className={`${prefixCls}-card-type-e__third-list`}>
                                            {thirdList.map((thirdItem: any, thirdIndex: number) => {
                                                const thirdKey = `${secondItem.id || secondIndex}_${thirdItem.id || thirdIndex}`;
                                                const isThirdActive = currentThirdIndex === thirdIndex;
                                                const isThirdOpen = openThirdKey === thirdKey;
                                                return (
                                                    <div className={`${prefixCls}-card-type-e__third`} key={thirdItem.id || thirdIndex}>
                                                        <button
                                                            className={`${prefixCls}-card-type-e__third-btn ${isThirdActive ? 'active' : ''} ${isThirdOpen ? 'open' : ''
                                                                } GtmModuleData-btn-common header-nvd-t3-i`}
                                                            type="button"
                                                            onClick={() => {
                                                                handleThirdActive(secondIndex, thirdIndex, secondItem, thirdItem);
                                                                setOpenThirdKey(isThirdOpen ? '' : thirdKey);
                                                                setExpandedProductKey('');
                                                            }}
                                                        >
                                                            {thirdItem.name}
                                                        </button>
                                                        {isThirdOpen && (
                                                            <div className={`${prefixCls}-card-type-e__h5-panel`}>
                                                                <div className={`${prefixCls}-card-type-e__third-info`}>
                                                                    {thirdItem.subtitle && (
                                                                        <div className={`${prefixCls}-card-type-e__third-desc`}>{thirdItem.subtitle}</div>
                                                                    )}
                                                                    {thirdItem.buttonName && thirdItem.buttonUrl && (
                                                                        <EcoLink
                                                                            className={`${prefixCls}-card-type-e__third-link GtmModuleData-btn-href type-e-third-link`}
                                                                            href={thirdItem.buttonUrl}
                                                                            target={getTarget(thirdItem.buttonUrl)}
                                                                        >
                                                                            {thirdItem.buttonName}
                                                                            <ThirdLinkArrow className={`${prefixCls}-card-type-e__third-link-arrow`} />
                                                                        </EcoLink>
                                                                    )}
                                                                </div>
                                                                <div className={`${prefixCls}-card-type-e__h5-products ${expandedProductKey === thirdKey ? 'expanded' : ''}`}>
                                                                    {(thirdItem.subs || []).map((product: any, productIndex: number) => (
                                                                        <ProductCard
                                                                            prefixCls={prefixCls}
                                                                            product={product}
                                                                            secondItem={secondItem}
                                                                            thirdItem={thirdItem}
                                                                            title={title}
                                                                            index={productIndex}
                                                                            key={product.id || productIndex}
                                                                        />
                                                                    ))}
                                                                </div>
                                                                {(thirdItem.subs || []).length > 5 && (
                                                                    <button
                                                                        className={`${prefixCls}-card-type-e__h5-more ${expandedProductKey === thirdKey ? 'active' : ''}`}
                                                                        type="button"
                                                                        aria-label={expandedProductKey === thirdKey ? 'Collapse products' : 'Show all products'}
                                                                        onClick={() => setExpandedProductKey(expandedProductKey === thirdKey ? '' : thirdKey)}
                                                                    />
                                                                )}
                                                                {thirdItem.buttonName && thirdItem.buttonUrl && (
                                                                    <EcoLink
                                                                        className={`${prefixCls}-card-type-e__h5-third-link GtmModuleData-btn-href type-e-third-link`}
                                                                        href={thirdItem.buttonUrl}
                                                                        target={getTarget(thirdItem.buttonUrl)}
                                                                    >
                                                                        {thirdItem.buttonName}
                                                                    </EcoLink>
                                                                )}
                                                            </div >
                                                        )}
                                                    </div >
                                                );
                                            })}
                                        </div >
                                    )}
                                </div >
                            );
                        })}
                    </div >
                    <div className={`${prefixCls}-card-type-e__right`}>
                        <div className={`${prefixCls}-card-type-e__right-head`}>
                            <div className={`${prefixCls}-card-type-e__right-action`}>
                                {activeThirdItem.subtitle && <div className={`${prefixCls}-card-type-e__third-desc`}>{activeThirdItem.subtitle}</div>}
                                {activeThirdItem.buttonName && activeThirdItem.buttonUrl && (
                                    <EcoLink
                                        className={`${prefixCls}-card-type-e__third-link GtmModuleData-btn-href type-e-third-link`}
                                        href={activeThirdItem.buttonUrl}
                                        target={getTarget(activeThirdItem.buttonUrl)}
                                    >
                                        {activeThirdItem.buttonName}
                                        <ThirdLinkArrow className={`${prefixCls}-card-type-e__third-link-arrow`} />
                                    </EcoLink>
                                )}
                            </div>
                        </div>
                        <div className={`${prefixCls}-card-type-e__products`} key={activeThirdItem?.id || activeThirdIndex}>
                            {productItems.map((product: any, index: number) => (
                                <ProductCard
                                    prefixCls={prefixCls}
                                    product={product}
                                    secondItem={activeSecondItem}
                                    thirdItem={activeThirdItem}
                                    title={title}
                                    index={index}
                                    key={product.id || index}
                                />
                            ))}
                        </div>
                    </div>
                </div >
            </div >
        </div >
    );
};

export default CardTypeE;
